import { handleSessionExpired, isAuthErrorStatus } from '../utils/sessionExpiry';

const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024;
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_BACKOFF_MS = 500;
const BLOB_REVOKE_DELAY_MS = 120_000;

export class ExportCacheExpiredError extends Error {
  constructor() {
    super('Export session expired on the server. Please try exporting again.');
    this.name = 'ExportCacheExpiredError';
  }
}

export interface ProjectFileDownloadOptions {
  authToken?: string | null;
  chunkSize?: number;
  maxRetries?: number;
  baseBackoffMs?: number;
  onProgress?: (downloadedBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
}

interface ChunkDescriptor {
  start: number;
  end: number;
}

interface ProbeResult {
  totalSize: number;
  /** Set when the server returned the full file on the probe (no Range support). */
  fullBody?: ArrayBuffer;
  filename?: string;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function buildChunks(totalSize: number, chunkSize: number): ChunkDescriptor[] {
  const chunks: ChunkDescriptor[] = [];
  for (let offset = 0; offset < totalSize; offset += chunkSize) {
    chunks.push({
      start: offset,
      end: Math.min(offset + chunkSize - 1, totalSize - 1),
    });
  }
  return chunks;
}

export function parseContentDispositionFilename(header: string | null | undefined): string | null {
  if (!header?.trim()) {
    return null;
  }
  const utf8Match = header.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, '').trim());
    } catch {
      return utf8Match[1].replace(/"/g, '').trim();
    }
  }
  const plainMatch = header.match(/filename="?([^";\n]+)"?/i);
  return plainMatch?.[1]?.trim() ?? null;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_REVOKE_DELAY_MS);
}

/** Trigger a browser file download from a Blob (Excel / other binary exports). */
export function downloadBlobFile(blob: Blob, filename: string): void {
  triggerBlobDownload(blob, filename);
}

function authHeader(authToken: string | null | undefined): Record<string, string> {
  const token = authToken?.trim();
  if (!token) {
    return {};
  }
  return {
    Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
  };
}

function checkAuthResponse(status: number): void {
  if (isAuthErrorStatus(status)) {
    handleSessionExpired();
    throw new Error('You are not authorized to export this project. Please sign in again.');
  }
}

/**
 * Chunked HTTP Range download for large project files (RFC 7233).
 */
export class ProjectFileDownloadManager {
  private readonly authToken: string | null;
  private readonly chunkSize: number;
  private readonly maxRetries: number;
  private readonly baseBackoffMs: number;
  private readonly onProgress: (downloadedBytes: number, totalBytes: number) => void;
  private readonly signal?: AbortSignal;

  constructor(opts: ProjectFileDownloadOptions = {}) {
    this.authToken = opts.authToken ?? null;
    this.chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE;
    this.maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.baseBackoffMs = opts.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS;
    this.onProgress = opts.onProgress ?? (() => {});
    this.signal = opts.signal;
  }

  async download(
    url: string,
    fallbackFilename: string,
    callOpts?: { onProgress?: (downloadedBytes: number, totalBytes: number) => void },
  ): Promise<{ filename: string }> {
    const progressCb = callOpts?.onProgress ?? this.onProgress;
    const probe = await this.probe(url);

    const filename = probe.filename || fallbackFilename;

    if (probe.fullBody) {
      progressCb(probe.fullBody.byteLength, probe.fullBody.byteLength);
      const blob = new Blob([probe.fullBody], { type: 'application/octet-stream' });
      triggerBlobDownload(blob, filename);
      return { filename };
    }

    const chunks = buildChunks(probe.totalSize, this.chunkSize);
    const buffers: ArrayBuffer[] = [];
    let downloaded = 0;
    progressCb(0, probe.totalSize);

    for (const { start, end } of chunks) {
      const buf = await this.fetchChunkWithRetry(url, start, end);
      buffers.push(buf);
      downloaded += buf.byteLength;
      progressCb(downloaded, probe.totalSize);
    }

    const blob = new Blob(buffers, { type: 'application/octet-stream' });
    triggerBlobDownload(blob, filename);
    return { filename };
  }

  private async probe(url: string): Promise<ProbeResult> {
    const resp = await this.fetchRange(url, 0, 0);
    const disposition = resp.headers.get('Content-Disposition');
    const filename = parseContentDispositionFilename(disposition) ?? undefined;

    if (resp.status === 200) {
      const contentLength = resp.headers.get('Content-Length');
      const buf = await resp.arrayBuffer();
      const totalSize = contentLength ? parseInt(contentLength, 10) : buf.byteLength;
      if (buf.byteLength > 1) {
        return { totalSize, fullBody: buf, filename };
      }
      return { totalSize, filename };
    }

    if (resp.status === 416) {
      throw new ExportCacheExpiredError();
    }

    if (resp.status !== 206) {
      throw new Error(`Export failed: server returned HTTP ${resp.status}`);
    }

    const contentRange = resp.headers.get('Content-Range');
    if (!contentRange) {
      throw new Error('Export failed: missing Content-Range header');
    }

    const match = contentRange.match(/bytes\s+\d+-\d+\/(\d+)/);
    if (!match) {
      throw new Error(`Export failed: unparseable Content-Range (${contentRange})`);
    }

    // Discard the single probe byte so the connection can be reused cleanly.
    await resp.arrayBuffer();

    return { totalSize: parseInt(match[1], 10), filename };
  }

  private async fetchChunkWithRetry(url: string, start: number, end: number): Promise<ArrayBuffer> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      if (attempt > 0) {
        await sleep(this.baseBackoffMs * 2 ** (attempt - 1));
      }
      try {
        const resp = await this.fetchRange(url, start, end);
        if (resp.status === 416) {
          throw new ExportCacheExpiredError();
        }
        if (resp.status === 206 || resp.status === 200) {
          return resp.arrayBuffer();
        }
        lastError = new Error(`Chunk ${start}-${end} failed: HTTP ${resp.status}`);
      } catch (err) {
        lastError = err;
        if (err instanceof ExportCacheExpiredError) {
          throw err;
        }
      }
    }
    throw lastError instanceof Error ? lastError : new Error('Export failed after retries');
  }

  private fetchRange(url: string, start: number, end: number): Promise<Response> {
    return fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream',
        Range: `bytes=${start}-${end}`,
        ...authHeader(this.authToken),
      },
      signal: this.signal,
    }).then((resp) => {
      checkAuthResponse(resp.status);
      return resp;
    });
  }
}

export function buildProjectFileExportUrl(projectId: string, baseUrl: string): string {
  const root = baseUrl.replace(/\/+$/, '');
  const params = new URLSearchParams({ projectId });
  return `${root}/dpr/apis/planning/projectFile?${params.toString()}`;
}
